import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileWarning, Plus, AlertTriangle } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { PolicySaysChip } from "../components/PolicySaysChip";
import { NextSteps } from "../components/NextSteps";
import { DetailFields, DetailSection } from "../components/RecordView";
import {
  EmptyState, Field, ModalHeader, ModalOverlay, MultiPick, StatusPill, dangerBtn, fmtDateOnly, input, relativeDue,
  secondaryBtn, tableHeadRow, textarea, type AttentionItem,
} from "./resilience/shared";

// Policy exceptions — time-limited departures from an adopted policy (backend/src/routes/policyExceptions.routes.ts,
// lib/policyExceptions.ts). Each is approved on My Tasks by someone other than the requester and rests on a
// risk acceptance the risk appetite rules police; on expiry it becomes a corrective action unless a renewal
// was approved in time. Nothing deletes an exception. Writing needs documents:write; reading is open.

type Standing = "pending" | "scheduled" | "live" | "expiring" | "lapsed" | "rejected" | "withdrawn" | "expired" | "ended" | "renewed";
interface Person { id: string; name: string | null }
interface ExceptionRow {
  id: string; ref: string; policyTemplateId: string; policyTitle: string; ruleReference: string | null; title: string;
  justification: string; compensatingControls: string; startsAt: string; expiresAt: string; status: string; statusLabel: string; standing: Standing;
  requestedBy: Person | null; owner: Person | null; approver: Person | null; selfApproved: boolean; decisionNote: string | null; decidedAt: string | null;
  risk: { id: string; ref: string; title: string; status: string; score: number | null } | null; riskCreated: boolean; riskLikelihood: number | null; riskImpact: number | null;
  renews: { id: string; ref: string; title: string } | null; renewals: Array<{ id: string; ref: string; status: string }>; renewalCount: number;
  expiredAt: string | null; endedAt: string | null; endReason: string | null;
  correctiveAction: { id: string; ref: string; status: string; dueDate: string | null } | null;
  subjects: Array<{ id: string; kind: "asset" | "supplier" | "member"; refId: string | null; name: string }>;
}
interface Params { maxMonths: number; reminderDays: number; renewalLimit: number; remediationDays: number }
interface View {
  exceptions: ExceptionRow[]; params: Params; attention: AttentionItem[];
  policies: Array<{ templateId: string; title: string }>;
  members: Array<{ id: string; name: string }>;
  risks: Array<{ id: string; ref: string; title: string; score: number | null }>;
  assets: Array<{ id: string; name: string }>; suppliers: Array<{ id: string; name: string }>;
  me: string;
}

const STANDING: Record<Standing, { label: string; pill: string }> = {
  pending: { label: "Awaiting approval", pill: "pending" },
  scheduled: { label: "Approved, not started", pill: "planned" },
  live: { label: "In force", pill: "active" },
  expiring: { label: "In force, expiring", pill: "in_progress" },
  lapsed: { label: "Expired", pill: "overdue" },
  rejected: { label: "Rejected", pill: "cancelled" },
  withdrawn: { label: "Withdrawn", pill: "cancelled" },
  expired: { label: "Expired", pill: "overdue" },
  ended: { label: "Ended early", pill: "closed" },
  renewed: { label: "Replaced by a renewal", pill: "closed" },
};
const KIND_LABEL: Record<string, string> = { asset: "Asset", supplier: "Supplier", member: "Person" };
const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };
const inForce = (s: Standing) => s === "live" || s === "expiring";

/** Today and a date some months on, as the UTC dates the server compares with (date-only fields travel as UTC midnight). */
const utcToday = () => new Date().toISOString().slice(0, 10);
function utcPlusMonths(from: string, months: number): string {
  const [y, m, d] = from.split("-").map(Number);
  const last = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, last))).toISOString().slice(0, 10);
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function PolicyExceptionsPage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("documents:write");
  const canRisk = !!user?.permissions?.includes("risks:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<View | null>(null);
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState<{ renews: ExceptionRow | null } | null>(null);
  const [show, setShow] = useState<"current" | "all">("current");

  const load = useCallback(async () => {
    try { setData(await api.get<View>("/policy-exceptions")); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the policy exceptions.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const openId = searchParams.get("exception");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("exception", id); else next.delete("exception");
    setSearchParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const all = data?.exceptions ?? [];
    return show === "all" ? all : all.filter((e) => e.standing === "pending" || e.standing === "scheduled" || inForce(e.standing));
  }, [data, show]);
  const open = data?.exceptions.find((e) => e.id === openId) ?? null;
  const yearAgo = Date.now() - 365 * 86_400_000;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <FileWarning size={20} /> Policy exceptions
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            A time-limited departure from a policy you have adopted{iso27001Citation(enrolled, "A.5.1")}: why the rule cannot be followed,
            what compensates, and when it ends. Someone other than the requester approves it on My Tasks, which records the risk
            acceptance on the risk register. When it expires the policy applies again; if it is still not met, a corrective action is raised.
          </p>
          <PolicySaysChip keys={["policy.exception_max_months", "policy.exception_reminder_days", "policy.exception_renewal_limit", "policy.exception_remediation_days"]} />
        </div>
        {canEdit && data && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} disabled={data.policies.length === 0} onClick={() => setRequesting({ renews: null })}>
            <Plus size={14} /> Request an exception
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}

      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Tile label="In force" value={data.exceptions.filter((e) => inForce(e.standing)).length} sub={`${data.exceptions.filter((e) => e.standing === "expiring").length} expiring within ${data.params.reminderDays} days`} />
            <Tile label="Awaiting approval" value={data.exceptions.filter((e) => e.standing === "pending").length} />
            <Tile label="Expired in 12 months" value={data.exceptions.filter((e) => e.status === "expired" && e.expiredAt && new Date(e.expiredAt).getTime() >= yearAgo).length} sub="each raised a corrective action" />
            <Tile label="Longest allowed" value={`${data.params.maxMonths} months`} sub={`renewals before escalation: ${data.params.renewalLimit}`} />
          </div>

          {data.policies.length === 0 && (
            <div style={{ ...card, padding: 16, ...text.body }}>
              No policy has been adopted yet, so there is nothing to make an exception to. Adopt a policy on{" "}
              <PortalLink to="/templates" style={{ color: "var(--c-accentText)" }}>Policy templates</PortalLink> first.
            </div>
          )}

          {data.attention.length > 0 && (
            <div style={{ ...card, padding: 0 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...text.sectionTitle, margin: 0 }}>
                <AlertTriangle size={15} /> Needs attention
                <span style={{ ...text.hint, fontWeight: 400 }}>{data.attention.length}</span>
              </div>
              {data.attention.map((a) => (
                <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={text.itemTitle}>{a.title}</div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                    </div>
                    {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                  </div>
                </PortalLink>
              ))}
            </div>
          )}

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "10px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ ...text.sectionTitle, margin: 0 }}>Register</span>
              <select style={{ ...input, width: "auto" }} value={show} onChange={(e) => setShow(e.target.value as typeof show)} aria-label="Which exceptions to show">
                <option value="current">Requested and in force</option>
                <option value="all">Everything, closed ones included</option>
              </select>
            </div>
            {rows.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState
                  title={data.exceptions.length ? "Nothing requested or in force" : "No policy exceptions yet"}
                  detail="When a rule in an adopted policy cannot be followed for a while, record it here with its compensating controls and an end date, and have someone else approve it."
                />
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={tableHeadRow}>
                  <th style={th}>Ref</th><th style={th}>Exception</th><th style={th}>Policy</th><th style={th}>Status</th><th style={th}>Expires</th><th style={th}>Owner</th><th style={th}>Risk</th>
                </tr></thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} onClick={() => setOpen(e.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{e.ref}</td>
                      <td style={{ ...td, color: theme.color.text }}>{e.title}{e.renews && <div style={text.hint}>Renews {e.renews.ref}</div>}</td>
                      <td style={td}>{e.policyTitle}{e.ruleReference && <div style={text.hint}>{e.ruleReference}</div>}</td>
                      <td style={td}><StatusPill value={STANDING[e.standing].pill} label={STANDING[e.standing].label} /></td>
                      <td style={{ ...td, color: e.standing === "expiring" ? theme.color.warningText : td.color }}>{fmtDateOnly(e.expiresAt)}</td>
                      <td style={td}>{e.owner?.name ?? "-"}</td>
                      <td style={td}>{e.risk ? e.risk.ref : e.status === "pending" ? "On approval" : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={text.hint}>An exception is a disclosed departure from the policy, not evidence that the requirement is met. Every exception is kept, whatever happens to it.</div>
        </div>
      )}

      {requesting && data && (
        <RequestException view={data} renews={requesting.renews} canRisk={canRisk} onClose={() => setRequesting(null)}
          onCreated={(id) => { setRequesting(null); void load(); setOpen(id); }} />
      )}
      {open && data && (
        <ExceptionView ex={open} view={data} canEdit={canEdit} onClose={() => setOpen(null)} onChanged={load}
          onRenew={() => { setOpen(null); setRequesting({ renews: open }); }} />
      )}
    </div>
  );
}

function RequestException({ view, renews, canRisk, onClose, onCreated }: {
  view: View; renews: ExceptionRow | null; canRisk: boolean; onClose: () => void; onCreated: (id: string) => void;
}) {
  const start = renews && new Date(renews.expiresAt).toISOString().slice(0, 10) >= utcToday()
    ? new Date(new Date(renews.expiresAt).getTime() + 86_400_000).toISOString().slice(0, 10)
    : utcToday();
  const [policyTemplateId, setPolicy] = useState(renews?.policyTemplateId ?? view.policies[0]?.templateId ?? "");
  const [ruleReference, setRule] = useState(renews?.ruleReference ?? "");
  const [title, setTitle] = useState(renews?.title ?? "");
  const [justification, setJustification] = useState(renews?.justification ?? "");
  const [compensatingControls, setCompensating] = useState(renews?.compensatingControls ?? "");
  const [startsAt, setStartsAt] = useState(start);
  const [expiresAt, setExpiresAt] = useState(utcPlusMonths(start, Math.min(3, view.params.maxMonths)));
  const [approverUserId, setApprover] = useState("");
  const [ownerUserId, setOwner] = useState(renews?.owner?.id ?? (view.members.some((m) => m.id === view.me) ? view.me : ""));
  const [riskMode, setRiskMode] = useState<"new" | "existing">(renews?.risk ? "existing" : "new");
  const [riskId, setRiskId] = useState(renews?.risk?.id ?? "");
  const [likelihood, setLikelihood] = useState(renews?.riskLikelihood ?? 2);
  const [impact, setImpact] = useState(renews?.riskImpact ?? 2);
  const [assetIds, setAssets] = useState<string[]>(renews?.subjects.filter((s) => s.kind === "asset" && s.refId).map((s) => s.refId!) ?? []);
  const [supplierIds, setSuppliers] = useState<string[]>(renews?.subjects.filter((s) => s.kind === "supplier" && s.refId).map((s) => s.refId!) ?? []);
  const [memberIds, setMembers] = useState<string[]>(renews?.subjects.filter((s) => s.kind === "member" && s.refId).map((s) => s.refId!) ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const latest = utcPlusMonths(startsAt || utcToday(), view.params.maxMonths);
  const approvers = view.members.filter((m) => m.id !== view.me || view.members.length === 1);
  const score = riskMode === "new" ? likelihood * impact : view.risks.find((r) => r.id === riskId)?.score ?? null;
  const steps = [
    { done: !!policyTemplateId, label: "The adopted policy departed from" },
    { done: title.trim().length >= 3, label: "A short title" },
    { done: justification.trim().length >= 20, label: "Why the policy cannot be followed" },
    { done: compensatingControls.trim().length >= 10, label: "The compensating controls in place meanwhile" },
    { done: !!expiresAt && expiresAt >= startsAt && expiresAt <= latest, label: `An expiry no later than ${fmtDateOnly(latest)}`, urgent: !!expiresAt && expiresAt > latest },
    { done: !!approverUserId, label: "An approver other than you" },
    { done: riskMode === "new" || !!riskId, label: riskMode === "new" ? "The risk it creates, scored" : "The risk register entry it accepts" },
  ];

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const res = await api.post<{ exception: ExceptionRow }>("/policy-exceptions", {
        policyTemplateId, ruleReference: ruleReference.trim() || null, title, justification, compensatingControls, startsAt, expiresAt,
        approverUserId, ownerUserId: ownerUserId || null,
        ...(riskMode === "existing" ? { riskId } : { riskLikelihood: likelihood, riskImpact: impact }),
        assetIds, supplierIds, memberIds, renewsId: renews?.id ?? null,
      });
      onCreated(res.exception.id);
    } catch (e) { setError(apiErrorMessage(e, "The request could not be made.")); }
    finally { setBusy(false); }
  };

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={renews ? `Renew ${renews.ref}` : "Request a policy exception"} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 14 }}>
        <NextSteps title="Before you send it" items={steps} />
        {renews && <div style={text.hint}>A renewal is a fresh request and a fresh approval. If {renews.ref} expires before this one is approved, its corrective action is still raised.</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          <Field label="Policy">
            <select style={input} value={policyTemplateId} disabled={!!renews} onChange={(e) => setPolicy(e.target.value)}>
              {view.policies.map((p) => <option key={p.templateId} value={p.templateId}>{p.title}</option>)}
            </select>
          </Field>
          <Field label="Rule or section" help="The policy's own numbering or words, so the approver can find the rule.">
            <input style={input} value={ruleReference} maxLength={300} onChange={(e) => setRule(e.target.value)} />
          </Field>
          <Field label="Title" span2><input style={input} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Why the policy cannot be followed" span2><textarea style={textarea} rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} /></Field>
          <Field label="Compensating controls" span2 help="What reduces the risk while the exception runs. Implement them before it starts.">
            <textarea style={textarea} rows={3} value={compensatingControls} onChange={(e) => setCompensating(e.target.value)} />
          </Field>
          <Field label="Starts"><input type="date" style={input} value={startsAt} min={utcToday()} onChange={(e) => setStartsAt(e.target.value)} /></Field>
          <Field label="Expires" help={`At most ${view.params.maxMonths} months after the start.`}>
            <input type="date" style={input} value={expiresAt} min={startsAt} max={latest} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
          <Field label="Approver" help="Someone other than you. Approving records the risk acceptance in their name.">
            <select style={input} value={approverUserId} onChange={(e) => setApprover(e.target.value)}>
              <option value="">Choose a member</option>
              {approvers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Owner" help="Accountable while it runs; reminded before it expires.">
            <select style={input} value={ownerUserId} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Choose a member</option>
              {view.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
        </div>

        <DetailSection title="Risk acceptance">
          <div style={{ padding: 14, display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", ...text.body }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="radio" checked={riskMode === "new"} onChange={() => setRiskMode("new")} /> Create a risk register entry on approval</label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", opacity: canRisk ? 1 : 0.6 }}>
                <input type="radio" checked={riskMode === "existing"} disabled={!canRisk} onChange={() => setRiskMode("existing")} /> Accept it on an existing risk
              </label>
            </div>
            {!canRisk && <div style={text.hint}>Naming an existing risk needs permission to change the risk register.</div>}
            {riskMode === "new" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                <Field label="Likelihood (1 to 5)" help="With the compensating controls in place.">
                  <select style={input} value={likelihood} onChange={(e) => setLikelihood(Number(e.target.value))}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                </Field>
                <Field label="Impact (1 to 5)">
                  <select style={input} value={impact} onChange={(e) => setImpact(Number(e.target.value))}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                </Field>
              </div>
            ) : (
              <Field label="Risk">
                <select style={input} value={riskId} onChange={(e) => setRiskId(e.target.value)}>
                  <option value="">Choose a risk</option>
                  {view.risks.map((r) => <option key={r.id} value={r.id}>{r.ref} {r.title}{r.score ? ` (score ${r.score})` : ""}</option>)}
                </select>
              </Field>
            )}
            <div style={text.hint}>
              {score ? `Score ${score}. ` : ""}Your risk appetite rules apply: a critical score cannot be accepted, and a score above appetite can be
              accepted only for as long as its band allows. The approver is told if the dates are too long.
            </div>
          </div>
        </DetailSection>

        <DetailSection title="What it covers">
          <div style={{ padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <Field label="Assets"><MultiPick options={view.assets} selected={assetIds} onChange={setAssets} empty="No assets in the register." /></Field>
            <Field label="Suppliers"><MultiPick options={view.suppliers} selected={supplierIds} onChange={setSuppliers} empty="No suppliers in the register." /></Field>
            <Field label="People"><MultiPick options={view.members} selected={memberIds} onChange={setMembers} /></Field>
          </div>
          <div style={{ ...text.hint, padding: "0 14px 14px" }}>Leave all three empty for an exception that applies across the organisation.</div>
        </DetailSection>

        {error && <div style={errorText}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" style={btnPrimary} disabled={busy || steps.some((s) => !s.done)} onClick={() => void submit()}>
            {busy ? "Sending..." : "Send for approval"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ExceptionView({ ex, view, canEdit, onClose, onChanged, onRenew }: {
  ex: ExceptionRow; view: View; canEdit: boolean; onClose: () => void; onChanged: () => void; onRenew: () => void;
}) {
  const [closing, setClosing] = useState<"withdraw" | "end" | null>(null);
  const [reason, setReason] = useState("");
  const [owner, setOwner] = useState(ex.owner?.id ?? "");
  const [error, setError] = useState("");
  const live = inForce(ex.standing) || ex.standing === "scheduled";
  const renewalInHand = ex.renewals.some((r) => r.status === "pending" || r.status === "approved");

  const act = async (path: string, body: object, fallback: string) => {
    setError("");
    try { await api.post(`/policy-exceptions/${ex.id}/${path}`, body); setClosing(null); setReason(""); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, fallback)); }
  };
  const reassign = async (id: string) => {
    setOwner(id); setError("");
    try { await api.patch(`/policy-exceptions/${ex.id}/owner`, { ownerUserId: id }); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "The owner could not be changed.")); }
  };

  const steps = ex.status === "pending"
    ? [{ done: false, label: `Waiting for ${ex.approver?.name ?? "the approver"} to decide on My Tasks` }]
    : live ? [
      { done: renewalInHand, label: `Bring the practice in line with the policy, or have a renewal approved, before ${fmtDateOnly(ex.expiresAt)}`, urgent: ex.standing === "expiring" },
      ...(ex.renewalCount > view.params.renewalLimit ? [{ done: false, label: `Renewed ${ex.renewalCount} times, beyond your limit of ${view.params.renewalLimit}: escalate it to top management`, urgent: true }] : []),
    ] : [];

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={`${ex.ref} ${ex.title}`} onClose={onClose} right={<StatusPill value={STANDING[ex.standing].pill} label={STANDING[ex.standing].label} />} />
      <div style={{ padding: 20, display: "grid", gap: 4 }}>
        <NextSteps items={steps} />
        <DetailSection title="The exception">
          <div style={{ padding: 14 }}>
            <DetailFields fields={[
              { label: "Policy", value: ex.policyTitle },
              { label: "Rule or section", value: ex.ruleReference },
              { label: "Runs", value: `${fmtDateOnly(ex.startsAt)} to ${fmtDateOnly(ex.expiresAt)}` },
              { label: "Covers", value: ex.subjects.length ? ex.subjects.map((s) => `${KIND_LABEL[s.kind]}: ${s.name}`).join("; ") : "The whole organisation" },
              { label: "Why the policy cannot be followed", value: ex.justification, wide: true, multiline: true },
              { label: "Compensating controls", value: ex.compensatingControls, wide: true, multiline: true },
            ]} />
          </div>
        </DetailSection>
        <DetailSection title="Approval and risk acceptance">
          <div style={{ padding: 14 }}>
            <DetailFields fields={[
              { label: "Requested by", value: ex.requestedBy?.name },
              { label: "Approver", value: `${ex.approver?.name ?? "-"}${ex.selfApproved ? " (sole member)" : ""}` },
              { label: "Decision", value: ex.decidedAt ? `${ex.statusLabel} ${fmtDateOnly(ex.decidedAt)}${ex.decisionNote ? `: ${ex.decisionNote}` : ""}` : "Not decided yet" },
              { label: "Risk", value: ex.risk
                ? <PortalLink to={`/risk-register?risk=${ex.risk.id}`} style={{ color: "var(--c-accentText)" }}>{ex.risk.ref} {ex.risk.title}{ex.risk.score ? ` (score ${ex.risk.score})` : ""}</PortalLink>
                : ex.status === "pending" ? `Created on approval${ex.riskLikelihood && ex.riskImpact ? `, score ${ex.riskLikelihood * ex.riskImpact}` : ""}` : null },
              { label: "Renews", value: ex.renews ? <PortalLink to={`/policy-exceptions?exception=${ex.renews.id}`} style={{ color: "var(--c-accentText)" }}>{ex.renews.ref}</PortalLink> : null },
              { label: "Renewals", value: ex.renewals.length ? ex.renewals.map((r) => `${r.ref} (${r.status})`).join(", ") : null },
            ]} />
          </div>
        </DetailSection>
        {(ex.expiredAt || ex.endedAt || ex.correctiveAction) && (
          <DetailSection title="How it ended">
            <div style={{ padding: 14 }}>
              <DetailFields fields={[
                ...(ex.expiredAt ? [{ label: "Expired", value: fmtDateOnly(ex.expiresAt) }] : []),
                ...(ex.endedAt ? [{ label: ex.status === "withdrawn" ? "Withdrawn" : "Ended", value: `${fmtDateOnly(ex.endedAt)}: ${ex.endReason ?? ""}` }] : []),
                ...(ex.correctiveAction ? [{ label: "Corrective action", value: <PortalLink to={`/improvement?action=${ex.correctiveAction.id}`} style={{ color: "var(--c-accentText)" }}>{ex.correctiveAction.ref} ({ex.correctiveAction.status}{ex.correctiveAction.dueDate ? `, due ${fmtDateOnly(ex.correctiveAction.dueDate)}` : ""})</PortalLink> }] : []),
              ]} />
            </div>
          </DetailSection>
        )}

        {error && <div style={errorText}>{error}</div>}
        {canEdit && (
          <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
            {(ex.status === "pending" || ex.status === "approved") && (
              <Field label="Owner">
                <select style={{ ...input, maxWidth: 320 }} value={owner} onChange={(e) => void reassign(e.target.value)}>
                  {!view.members.some((m) => m.id === owner) && <option value={owner}>{ex.owner?.name ?? "Nobody"} (no longer active)</option>}
                  {view.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
            )}
            {closing ? (
              <div style={{ display: "grid", gap: 8 }}>
                <Field label={closing === "withdraw" ? "Why is the request being withdrawn?" : "How does the practice now follow the policy?"}>
                  <textarea style={textarea} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={secondaryBtn} onClick={() => setClosing(null)}>Back</button>
                  <button type="button" style={dangerBtn} disabled={reason.trim().length < 10}
                    onClick={() => void act(closing, { reason }, closing === "withdraw" ? "The request could not be withdrawn." : "The exception could not be ended.")}>
                    {closing === "withdraw" ? "Withdraw request" : "End the exception"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                {ex.status === "pending" && <button type="button" style={dangerBtn} onClick={() => setClosing("withdraw")}>Withdraw request</button>}
                {ex.status === "approved" && <button type="button" style={dangerBtn} onClick={() => setClosing("end")}>End early</button>}
                {(ex.status === "approved" || ex.status === "expired") && !renewalInHand && <button type="button" style={secondaryBtn} onClick={onRenew}>Ask for a renewal</button>}
              </div>
            )}
          </div>
        )}
        <div style={{ ...text.hint, marginTop: 8 }}>An exception is kept whatever happens to it; nothing deletes one.</div>
      </div>
    </ModalOverlay>
  );
}
